import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { APICallService } from 'src/app/services/api-call.service';
import { FormService } from 'src/app/services/form.service';
import { UserDetailsService } from 'src/app/services/user-details.service';
import { SecurityService } from 'src/app/services/security.service';
import { JsonDataService } from 'src/app/services/json-data.service';
import { AppMessageService } from 'src/app/services/app-message.service';

interface QRRecord {
    BM_CODE: string;
    AGENT_NAME?: string;
    ISS_CD?: string;
    QUOTE_NO?: string;
    POLICY_NO?: string;
    GROSS_PREM?: number;
    NET_PREM?: number;
    QRID?: string;
    CREATE_DATE?: string;
}

@Component({
    selector: 'generate-qr-report',
    templateUrl: './generate-qr-report.component.html',
    styleUrls: ['./generate-qr-report.component.css']
})
export class GenerateQRReport implements OnInit {

    reportParams!: FormGroup;
    qrReportList: QRRecord[] = [];
    pagedQRReportList: QRRecord[] = [];

    currentPage: number = 1;
    pageSize: number = 10;
    totalPages: number = 0;

    constructor(
        private fb: FormBuilder,
        private api: APICallService,
        private dialog: MatDialog,
        private router: Router,
        private formService: FormService,
        private userDetails: UserDetailsService,
        private securityService: SecurityService,
        private jsonData: JsonDataService,
        private appMessageService: AppMessageService,
    ) {}

    ngOnInit(): void {
        this.loadQRReport();
    }

    loadQRReport(): void {
        const payload = {};

        this.api.getQRRecords(payload).subscribe({
            next: (response: any) => {
                this.securityService.checkRequestKeyResponse(response, () => {
                    this.securityService.hasValidCsrfToken(response, () => {
                        if (response.status === 'SUCCESS') {

                            let content: QRRecord[] | { data: QRRecord[] } = [];
                            try {
                                content = JSON.parse(this.jsonData.decrypt(response.response));
                            } catch (e) {
                                console.error('Failed to parse/decrypt QR response', e);
                                this.qrReportList = [];
                                this.appMessageService.showAppMessage(
                                    'Invalid QR report data received.',
                                    'error'
                                );
                                return;
                            }

                            const list: QRRecord[] = Array.isArray(content)
                                ? content
                                : Array.isArray(content.data)
                                    ? content.data
                                    : [];

                            this.qrReportList = list.filter(item => !!item && !!item.BM_CODE);

                            this.totalPages = Math.ceil(this.qrReportList.length / this.pageSize);
                            this.currentPage = 1;
                            this.updatePagedList();

                        } else {
                            this.qrReportList = [];
                            this.pagedQRReportList = [];
                            this.appMessageService.showAppMessage(
                                'Failed to load QR report.',
                                'error'
                            );
                        }
                    });
                });
            },
            error: (err: any) => {
                console.error(err);
                this.qrReportList = [];
                this.pagedQRReportList = [];
                this.appMessageService.showAppMessage(
                    'Failed to load QR report. Please try again.',
                    'error'
                );
            }
        });
    }

    updatePagedList(): void {
        const start = (this.currentPage - 1) * this.pageSize;
        const end = start + this.pageSize;
        this.pagedQRReportList = this.qrReportList.slice(start, end);
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.updatePagedList();
        }
    }

    prevPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.updatePagedList();
        }
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.updatePagedList();
        }
    }

    min(a: number, b: number): number {
        return Math.min(a, b);
    }

    pages(): number[] {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }
}
